var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useContext, useState } from "react";
import { Icon, IconName, Panel, PanelSize, Stack } from "@eightfold.ai/octuple";
import styles from "./Additional.module.scss";
import { useGetClientDetailsQuery, useGetClientPlatformQuery, } from "../../store/api/core";
import { usePermissions } from "../../hooks/permissions";
import { haveTalentReviewPages } from "../../common/featureFlags";
import Button from "../Button";
import ResetButton from "../ResetButton";
import { Divider } from "@mui/material";
import { FilterContext, getFilters, updateFilters, } from "../../providers/FilterProvider";
import CheckBox from "../CheckBox";
import { AvailableFilters, getEntity } from "../../common/filters";
import { getFiltersAsArray } from "./Filters";
var filterOrder = [
    AvailableFilters.CareerInterests,
    AvailableFilters.AssessmentSource,
    AvailableFilters.AgeRange,
    AvailableFilters.DerailerRisk,
    AvailableFilters.Gender,
    AvailableFilters.Race,
    AvailableFilters.Ninebox,
    AvailableFilters.Assessed,
    AvailableFilters.NineboxRatings,
    AvailableFilters.CareerTrack,
    AvailableFilters.RetentionRisk,
    AvailableFilters.LossImpact,
    AvailableFilters.WillingToRelocate,
    AvailableFilters.SecurityClearance,
    AvailableFilters.Retirement,
    // order guessed after this point
    AvailableFilters.Industries,
    AvailableFilters.HighPotentialLeader,
    AvailableFilters.HierarchyLeaderProfileIds,
    AvailableFilters.CompanyName,
];
var Header = function () { return (_jsx("div", __assign({ className: styles.panelHeader }, { children: "Additional Filters" }))); };
var Body = function (_a) {
    var _b, _c;
    var filters = _a.filters;
    var _d = usePermissions(), nineBox = _d.nineBox, showDiversitySpecific = _d.showDiversitySpecific;
    var filtersVisibleValues = useGetClientPlatformQuery({});
    var hasPermission = function (_a) {
        var _b, _c, _d, _e, _f;
        var key = _a.key;
        return !((key == AvailableFilters.Race && !showDiversitySpecific) ||
            (key == AvailableFilters.Gender && !showDiversitySpecific) ||
            (key == AvailableFilters.Ninebox && !(nineBox && haveTalentReviewPages()))) &&
            ((_f = (_e = (_d = (_c = (_b = filtersVisibleValues === null || filtersVisibleValues === void 0 ? void 0 : filtersVisibleValues.data) === null || _b === void 0 ? void 0 : _b.data[0]) === null || _c === void 0 ? void 0 : _c.applications) === null || _d === void 0 ? void 0 : _d.orgDashboard) === null || _e === void 0 ? void 0 : _e.filters) === null || _f === void 0 ? void 0 : _f[key]) !== false;
    };
    var componentFilters = getFiltersAsArray(filterOrder.filter(function (key) { return hasPermission({ key: key }); }), filters)({});
    var handleCheckboxChange = function (event) {
        var _a, _b;
        var isChecked = event.target.checked;
        if (isChecked) {
            updateFilters((_a = {},
                _a[getEntity(AvailableFilters.HighPotentialLeader)] = "Yes",
                _a));
        }
        else {
            updateFilters((_b = {},
                _b[getEntity(AvailableFilters.HighPotentialLeader)] = undefined,
                _b));
        }
    };
    return (_jsxs("div", __assign({ className: styles.body }, { children: [_jsx("div", __assign({ className: styles.highPotentialTalentCheckBoxWrapper }, { children: _jsx(CheckBox, { value: ((_b = getFilters()) === null || _b === void 0 ? void 0 : _b[getEntity(AvailableFilters.HighPotentialLeader)]) ||
                        "No", label: "See only high potential talent", checked: ((_c = getFilters()) === null || _c === void 0 ? void 0 : _c[getEntity(AvailableFilters.HighPotentialLeader)]) ===
                        "Yes", onChange: handleCheckboxChange }) })), _jsx(Stack, __assign({ direction: "vertical", flexGap: "s" }, { children: componentFilters.map(function (filter) { return (_jsx("div", { children: filter.implementation }, filter.key)); }) }))] })));
};
var AdditionalFilterButton = function (_a) {
    var onClick = _a.onClick, counter = _a.counter;
    return (_jsxs("div", __assign({ className: styles.additionalButtonContainer, style: __assign({}, (counter > 0 && {
            backgroundColor: "#EBFDFF",
        })), onClick: onClick }, { children: [counter > 0 && (_jsx("div", __assign({ className: styles.additionalButtonCounter }, { children: counter }))), _jsx(Icon, { path: IconName.mdiTuneVariant }), _jsx("button", __assign({ className: styles.additionalButton }, { children: "Additional Filters" }))] })));
};
export var Additional = function (_a) {
    var _b, _c, _d, _e, _f;
    var filters = _a.filters;
    var _g = useState(false), visible = _g[0], setVisible = _g[1];
    var _h = useState(0), resetKey = _h[0], setResetKey = _h[1];
    var onClick = function () { return setVisible(function (previous) { return !previous; }); };
    var getFilters = useContext(FilterContext).getFilters;
    var clientDetailsResponse = useGetClientDetailsQuery({});
    var hideGenderEthnicity = (_b = clientDetailsResponse.data) === null || _b === void 0 ? void 0 : _b.data.hide_gender_and_ethnicity;
    var clearFilters = function () {
        var _a;
        updateFilters((_a = {
                retentionRisk: [],
                careerInterests: [],
                assessmentSource: [],
                ageRange: [],
                derailerRisk: [],
                willingToRelocate: [],
                gender: [],
                ethnicity: [],
                ninebox_id: undefined
            },
            _a[getEntity(AvailableFilters.HighPotentialLeader)] = undefined,
            _a.assigned_box = [],
            _a.nineboxRating = [],
            _a.assessed = [],
            _a.careerTrack = [],
            _a.securityClearance = [],
            _a.retirement = [],
            _a.lossImpact = [],
            _a));
        setResetKey(function (prevKey) { return prevKey + 1; });
    };
    if (hideGenderEthnicity &&
        ((((_d = (_c = getFilters()) === null || _c === void 0 ? void 0 : _c.gender) === null || _d === void 0 ? void 0 : _d.length) || 0) > 0 ||
            (((_f = (_e = getFilters()) === null || _e === void 0 ? void 0 : _e.ethnicity) === null || _f === void 0 ? void 0 : _f.length) || 0) > 0)) {
        updateFilters({
            gender: [],
            ethnicity: [],
        });
    }
    var countSelectedAdditionalFilters = function () {
        var currentFilters = getFilters();
        var additionalFilters = new Set([
            AvailableFilters.RetentionRisk,
            AvailableFilters.CareerInterests,
            AvailableFilters.AssessmentSource,
            AvailableFilters.AgeRange,
            AvailableFilters.DerailerRisk,
            AvailableFilters.WillingToRelocate,
            AvailableFilters.Ninebox,
            AvailableFilters.NineboxRatings,
            AvailableFilters.Assessed,
            AvailableFilters.CareerTrack,
            AvailableFilters.SecurityClearance,
            AvailableFilters.Retirement,
            AvailableFilters.LossImpact,
            !hideGenderEthnicity && AvailableFilters.Gender,
            !hideGenderEthnicity && AvailableFilters.Race,
        ]);
        var selectedCount = 0;
        additionalFilters.forEach(function (key) {
            var value = currentFilters[getEntity(key)];
            if (Array.isArray(value)
                ? value.length > 0
                : value && value !== "No" && value !== "undefined") {
                selectedCount++;
            }
        });
        return selectedCount;
    };
    return (_jsxs(_Fragment, { children: [visible && (_jsxs(Panel, __assign({ placeholder: null, bodyPadding: false, size: PanelSize.small, visible: true, onClose: function () {
                    setVisible(false);
                } }, { children: [_jsx(Header, {}), _jsx(Divider, {}), _jsxs("div", __assign({ className: styles.additionalFilterContainer }, { children: [_jsx(Body, { filters: filters }), _jsxs("div", { children: [_jsx(Divider, {}), _jsxs("div", __assign({ className: styles.additionalFilterButtonsContainer }, { children: [_jsx(Button, { text: "Done", onClick: function () { return setVisible(false); } }), _jsx(ResetButton, { label: "Reset", onClick: clearFilters })] }))] })] }), resetKey)] }))), _jsx(AdditionalFilterButton, { counter: countSelectedAdditionalFilters(), onClick: onClick })] }));
};
